import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useAuth } from "@/auth/AuthContext";

type AdminUser = {
  id: number;
  username: string;
  email: string | null;
  role: string;
  totp_enrolled: boolean;
  created_at: string;
  last_login_at: string | null;
};

const ROLE_BADGE: Record<string, string> = {
  super_admin: "badge-gold",
  operator: "badge-ok",
  viewer: "badge-warn",
};

export function Users() {
  const [rows, setRows] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const toast = useToast();
  const { me } = useAuth();

  async function load() {
    try {
      setRows(await api<AdminUser[]>("/api/admin/users"));
    } catch (e: any) {
      setErr(e?.message ?? "load failed");
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function changeRole(u: AdminUser, role: string) {
    try {
      await api(`/api/admin/users/${u.id}/role`, {
        method: "PATCH",
        json: { role },
      });
      toast.success(`${u.username}: role → ${role}`);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "role change failed");
    }
  }

  async function remove(u: AdminUser) {
    if (!confirm(`Delete user ${u.username}? This cannot be undone.`)) return;
    try {
      await api(`/api/admin/users/${u.id}`, { method: "DELETE" });
      toast.success(`${u.username} deleted`);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "delete failed");
    }
  }

  const isSuperAdmin = me?.role === "super_admin";

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl display">Users</h1>
          <p className="text-sm text-slate-400">
            Super-admins manage the user list here. <b>operator</b> can rotate / issue /
            revoke, <b>viewer</b> is read-only. Only super-admins can delete real keys or
            manage other users.
          </p>
        </div>
        {isSuperAdmin && (
          <button className="btn-primary" onClick={() => setCreateOpen(true)}>
            + Add user
          </button>
        )}
      </header>

      {!isSuperAdmin && (
        <div className="card text-amber-300 text-sm">
          You need super-admin privileges to manage users.
        </div>
      )}

      {err && <div className="card text-rose-300 text-sm">{err}</div>}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500 uppercase tracking-wide bg-white/5">
            <tr>
              <th className="py-3 px-4">Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>TOTP</th>
              <th>Last login</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={6} className="py-4 px-4 text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {rows?.map((u) => (
              <tr key={u.id} className="border-t border-white/5">
                <td className="py-3 px-4 font-medium">
                  {u.username}
                  {u.id === me?.id && (
                    <span className="ml-2 text-[10px] text-slate-500">(you)</span>
                  )}
                </td>
                <td className="text-slate-400">{u.email ?? "—"}</td>
                <td>
                  <span className={ROLE_BADGE[u.role] ?? "badge"}>{u.role}</span>
                </td>
                <td>
                  {u.totp_enrolled ? (
                    <span className="badge-ok">enrolled</span>
                  ) : (
                    <span className="badge-warn">not enrolled</span>
                  )}
                </td>
                <td className="text-slate-500 text-xs">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                </td>
                <td className="text-right pr-4">
                  {isSuperAdmin && u.id !== me?.id && (
                    <>
                      <select
                        className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs mr-2"
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                      >
                        <option value="super_admin">super_admin</option>
                        <option value="operator">operator</option>
                        <option value="viewer">viewer</option>
                      </select>
                      <button
                        className="text-xs text-slate-400 hover:text-accent-500 mr-2"
                        onClick={() => setResetting(u)}
                      >
                        Reset pw
                      </button>
                      <button
                        className="text-xs text-rose-400 hover:text-rose-300"
                        onClick={() => remove(u)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} title="Add user" onClose={() => setCreateOpen(false)}>
        <CreateUserForm
          onCreated={async () => {
            setCreateOpen(false);
            toast.success("User created");
            await load();
          }}
        />
      </Modal>

      <Modal
        open={!!resetting}
        title={resetting ? `Reset password for ${resetting.username}` : ""}
        onClose={() => setResetting(null)}
      >
        {resetting && (
          <ResetPasswordForm
            userId={resetting.id}
            onDone={() => {
              setResetting(null);
              toast.success("Password reset");
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function CreateUserForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    role: "operator",
  });
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api("/api/admin/users", {
        method: "POST",
        json: {
          username: form.username,
          email: form.email || null,
          password: form.password,
          role: form.role,
        },
      });
      onCreated();
    } catch (e: any) {
      setErr(e?.message ?? "create failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <label className="block">
        <div className="text-xs text-slate-400 mb-1">Username</div>
        <input
          className="input"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          required
          autoFocus
        />
      </label>
      <label className="block">
        <div className="text-xs text-slate-400 mb-1">Email (optional)</div>
        <input
          className="input"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </label>
      <label className="block">
        <div className="text-xs text-slate-400 mb-1">
          Password (12+ chars, upper + lower + digit + symbol)
        </div>
        <input
          className="input mono"
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
          minLength={12}
        />
      </label>
      <label className="block">
        <div className="text-xs text-slate-400 mb-1">Role</div>
        <select
          className="input"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        >
          <option value="viewer">viewer — read-only</option>
          <option value="operator">operator — rotate/issue/revoke</option>
          <option value="super_admin">super_admin — full control</option>
        </select>
      </label>
      {err && <div className="text-rose-300 text-xs">{err}</div>}
      <div className="flex justify-end">
        <button className="btn-primary" disabled={loading} type="submit">
          {loading ? "Saving…" : "Add user"}
        </button>
      </div>
    </form>
  );
}

function ResetPasswordForm({
  userId,
  onDone,
}: {
  userId: number;
  onDone: () => void;
}) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api(`/api/admin/users/${userId}/reset-password`, {
        method: "POST",
        json: { new_password: pw },
      });
      onDone();
    } catch (e: any) {
      setErr(e?.message ?? "reset failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-400">
        Paste the new password. Every outstanding session belonging to this user
        is invalidated immediately — the reset works like a force-logout on all
        their devices.
      </p>
      <input
        className="input mono"
        type="password"
        autoComplete="new-password"
        minLength={12}
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        required
        autoFocus
      />
      {err && <div className="text-rose-300 text-xs">{err}</div>}
      <div className="flex justify-end">
        <button className="btn-primary" disabled={loading}>
          {loading ? "Saving…" : "Reset password"}
        </button>
      </div>
    </form>
  );
}
